import * as React from 'react'
import { useQuery } from '@apollo/react-hooks'
import { GetServerSidePropsContext } from 'next'
import getConfig from 'next/config'
import Head from 'next/head'

import {
  getContentRoutePathname,
  getSafeUrlParametersFromWindow,
  i18n,
  Routes,
  useSiteDefinition,
  withCacheConfiguration,
} from '@thg-commerce/enterprise-core'
import { PageCacheConfiguration } from '@thg-commerce/enterprise-core/src/cache/configuration'
import { loadConfiguration } from '@thg-commerce/enterprise-core/src/ConfigurationLoader/ConfigurationLoader'
import { NextConfig } from '@thg-commerce/enterprise-core/src/ConfigurationLoader/types'
import { createApolloWithRequest } from '@thg-commerce/enterprise-core/src/Factory/apollo'
import { ContentHeaderLayout } from '@thg-commerce/enterprise-core/src/Layouts'
import { EnterpriseRequest } from '@thg-commerce/enterprise-core/src/Server/types'
import { NetworkClient } from '@thg-commerce/enterprise-network'
import {
  AsgardPost,
  ContentSearchArgs,
} from '@thg-commerce/enterprise-network/types/enterpriseTypes'
import { spacing, styled, Text } from '@thg-commerce/enterprise-theme'
import { Button, visuallyHiddenMixin } from '@thg-commerce/gravity-elements'
import { Grid, GridItem } from '@thg-commerce/gravity-system'

import { InfinitePosts } from '../../src/components'
import {
  BlogNavigation,
  loadBlogNavigation,
} from '../../src/fetchers/blogNavigation'
import { ContentSearch as CONTENT_SEARCH_QUERY } from '../../src/graphql/ContentSearch.graphql'
import { SearchSeo as SEARCH_SEO } from '../../src/graphql/SearchSeo.graphql'

const POST_LIMIT = 10

const Heading = styled.h1`
  ${Text('xlarge1', 'alternate')}
  margin-top: ${spacing(7)};
  text-align: center;
`

const SearchForm = styled.form`
  display: flex;
  width: 100%;
  height: 40px;
  margin: ${spacing(2)} 0 ${spacing(4)};
  border-radius: 2px;
  background-color: ${(props) => props.theme.colors.palette.greys.white};
`

const SearchInput = styled.input`
  ${Text('large1', 'default')}
  background: transparent;
  padding: ${spacing(1)};
  flex-grow: 1;
  border: 2px solid transparent;
  text-align: center;

  &:focus-visible {
    outline: none;
    border-color: ${(props) => props.theme.colors.palette.greys.darker};
  }
`

const HiddenLabel = styled.label`
  ${visuallyHiddenMixin}
`

const NoResults = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const NoResultsText = styled.p`
  ${Text('bodyText', 'default')}
  margin-bottom: ${spacing(1)};
  text-align: center;
`
const StyledGrid = styled(Grid)`
  margin: 0px ${spacing(4)};
`

const SearchPage = (props: { seoData: any }) => {
  const { asgardToken, contentSlug, contentRoutePrefix } = useSiteDefinition()
  const { search } =
    typeof window !== 'undefined' &&
    (getSafeUrlParametersFromWindow(window, ['search']) as any)

  const { data, refetch, loading } = useQuery<
    { contentSearch: AsgardPost[] },
    ContentSearchArgs
  >(CONTENT_SEARCH_QUERY, {
    variables: {
      search: search || '',
      offset: 0,
      token: asgardToken || '',
      limit: POST_LIMIT,
      link: contentSlug || '',
    },
    ssr: false,
  })

  const [offset, setOffset] = React.useState<number>(0)
  const [displayShowMore, setDisplayShowMore] = React.useState<boolean>(true)

  const [searchResults, setSearchResults] = React.useState<AsgardPost[]>(
    data?.contentSearch || [],
  )

  React.useEffect(() => {
    if (data?.contentSearch && data.contentSearch.length < POST_LIMIT) {
      setDisplayShowMore(false)
    }

    data?.contentSearch &&
      setSearchResults((existingPosts) => [
        ...existingPosts,
        ...data.contentSearch,
      ])
  }, [data])

  const searchUrl = getContentRoutePathname(
    contentRoutePrefix,
    Routes.BlogSearch,
  )
  const searchUri =
    searchUrl.charAt(searchUrl.length - 1) === '/'
      ? searchUrl.slice(0, -1)
      : searchUrl

  const i18nText = {
    buttonTitle: i18n('header.contentsearchbutton.title'),
    formPlaceholder: i18n('header.contentsearchform.placeholder'),
    formTitle: i18n('header.contentsearchform.title'),
    resultsHeader: i18n('content.searchresults.title'),
    resultsNotFound: i18n('content.searchresultsnotfound.text'),
    resultsNextStep: i18n('content.searchresultsnotfound.nextstep'),
  }

  return (
    <React.Fragment>
      <Head>
        <title>{props.seoData?.pageSeo?.title}</title>
        {props.seoData?.pageSeo?.structuredDataJsonObject && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: props.seoData?.pageSeo?.structuredDataJsonObject,
            }}
          />
        )}
      </Head>
      <StyledGrid>
        <GridItem colSpan={[12, 12, 6, 6]} colStart={[1, 1, 4, 4]}>
          <Heading>{i18nText.resultsHeader}</Heading>
          <SearchForm action={searchUri} method="GET">
            <HiddenLabel htmlFor="article-search">
              {i18nText.formPlaceholder}
            </HiddenLabel>
            <SearchInput
              placeholder={i18nText.formPlaceholder}
              title={i18nText.formTitle}
              id="article-search"
              type="search"
              name="search"
              defaultValue={search ? decodeURIComponent(search) : ''}
            />
          </SearchForm>
        </GridItem>
        <GridItem colSpan={[12, 12, 6, 6]} colStart={[1, 1, 4, 4]}>
          {searchResults.length > 0 || loading ? (
            <InfinitePosts
              offset={offset}
              posts={searchResults || []}
              refetch={(offset) => {
                refetch({
                  search,
                  offset,
                  token: asgardToken || '',
                  limit: POST_LIMIT,
                  link: contentSlug || '',
                })
                setOffset(offset)
              }}
              postLimit={POST_LIMIT}
              displayShowMore={displayShowMore}
              isAmp={false}
              alternativeImageStyle={false}
              direction={'row'}
              margin={[spacing(2), spacing(2), spacing(2), spacing(4)]}
            />
          ) : (
            <NoResults>
              <NoResultsText>{i18nText.resultsNotFound}</NoResultsText>
              <Button emphasis="low" renderedAs="a" href="#article-search">
                {i18nText.resultsNextStep}
              </Button>
            </NoResults>
          )}
        </GridItem>
      </StyledGrid>
    </React.Fragment>
  )
}

SearchPage.Layout = ContentHeaderLayout

export const getServerSideProps = withCacheConfiguration<
  { props: { blogNavigation: BlogNavigation; pageSeo: any } },
  GetServerSidePropsContext
>(
  async (
    context: GetServerSidePropsContext,
  ): Promise<{ props: {}; cache?: PageCacheConfiguration }> => {
    const nextConfig = getConfig() as NextConfig
    const request = context.req as EnterpriseRequest
    const config = await loadConfiguration(nextConfig, request)
    const domain = config.publicRuntimeConfig.siteDefinition.domain

    const apollo = createApolloWithRequest(request, config, true)

    const blogNavigation = await loadBlogNavigation(config, apollo)

    const pageSeo = await apollo.query({
      query: SEARCH_SEO,
      context: { clientName: NetworkClient.Content },
    })

    return {
      props: {
        blogNavigation,
        pageSeo: pageSeo.data.pageSeo,
      },
      cache: {
        ttl: 1800, // 30 minutes
        grace: 86400, // 1 Day
        config: {
          domain,
        },
      },
    }
  },
)

export default SearchPage
